/**
 * The things the AI can do with the graph. Each one only suggests: nothing changes until
 * the suggestions are applied below.
 */
import { For, Show, type JSX } from "solid-js";
import type { AiTask } from "@protocol/AiTask";
import { useApp } from "../../app/context";

/** One task: its button label, the label while it runs, and what it does. */
const TASKS: { task: AiTask; label: string; running: string; what: string }[] = [
  { task: "enrich", label: "Enrich", running: "Enriching…", what: "after a scan: descriptions, groups, categories, kinds, missing links, and nodes for code no plugin reads" },
  { task: "notes", label: "Write notes", running: "Writing…", what: "a one-line description for nodes that have none" },
  { task: "groups", label: "Propose groups", running: "Grouping…", what: "groups (and sub-groups) of nodes that belong together" },
  { task: "review", label: "Review", running: "Reviewing…", what: "problems and advice about the architecture" },
];

/** The task buttons, and why the last request failed. */
export function TasksSection(): JSX.Element {
  const { ai, graph } = useApp();
  return (
    <section class="grp">
      <h3>Ask the AI</h3>
      <Show when={ai.ready()} fallback={<p class="small muted">Choose a service above first.</p>}>
        <p class="small muted">
          Sends the graph ({graph.state.graph.nodes.length} nodes: names, kinds, notes, links, groups) to {ai.settings()?.base_url}. Suggestions are shown first; you choose what to keep.
        </p>
      </Show>
      <ul class="ai-tasks">
        <For each={TASKS}>
          {(t) => (
            <li>
              <button type="button" class="btn" data-ai-task={t.task} disabled={!ai.ready() || !!ai.busy()} onClick={() => void ai.run(t.task)}>
                {ai.busy() === t.task ? t.running : t.label}
              </button>
              <span class="small muted">{t.what}</span>
            </li>
          )}
        </For>
      </ul>
      <Show when={ai.busy() && ai.busy() !== "save" && ai.busy() !== "test"}>
        <p class="small muted" id="ai-running">
          Waiting for the AI; this can take a minute.
        </p>
      </Show>
      <Show when={ai.error()}>
        <p class="small err" id="ai-error">
          {ai.error()}
        </p>
      </Show>
    </section>
  );
}
